import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", gender: "male", title: "", phone: "", email: "", age_years: "", age_months: "", age_days: "", address: "", city: "", id_type: "", id_number: "", medical_history: "" };

export default function LabPatients() {
  const { can } = useAuth();
  const manage = can("lab.patients.manage");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = async (p = page, query = q) => {
    setItems(null);
    try { const { data } = await api.get("/lab/patients", { params: { q: query || undefined, page: p, limit: 20 } }); setItems(data.items); setTotal(data.total); }
    catch (e) { setError(apiError(e)); }
  };
  useEffect(() => { load(1, ""); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p, age_years: p.age_years ?? "", age_months: p.age_months ?? "", age_days: p.age_days ?? "" }); setFormError(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError("Patient name is required."); return; }
    setBusy(true); setFormError("");
    try {
      const payload = { ...form, age_years: form.age_years === "" ? null : Number(form.age_years), age_months: form.age_months === "" ? null : Number(form.age_months), age_days: form.age_days === "" ? null : Number(form.age_days) };
      if (editing) await api.patch(`/lab/patients/${editing.patient_id}`, payload);
      else await api.post("/lab/patients", payload);
      toast.success(editing ? "Patient updated." : "Patient registered."); setOpen(false); load(1, q);
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };

  if (!can("lab.patients.view")) return <NoAccess what="Lab patients" />;
  const pages = Math.max(1, Math.ceil(total / 20));

  return (
    <div data-testid="lab-patients-page">
      <PageHeader overline="Cases" title="Patients"
        subtitle="Lab patients — mobile numbers are not unique; identity is the UHID. Register infants with age in months/days."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-patient-btn"><Plus className="h-4 w-4" /> Register patient</button>} />
      <form onSubmit={(e) => { e.preventDefault(); setPage(1); load(1, q); }} className="flex gap-3 mb-5">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / mobile / UHID…" className={`${inputCls} max-w-sm`} data-testid="patient-search" />
        <button className={ghostBtnCls} data-testid="patient-search-btn">Search</button>
      </form>
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <>
          <Table testid="lab-patients-table" columns={["UHID", "Name", "Age/Sex", "Mobile", "Address", "Registered", manage ? "Actions" : ""]}
            rows={items} empty={<EmptyState title="No patients" subtitle="Register your first patient." testid="lab-patients-empty" />}
            renderRow={(p) => (
              <tr key={p.patient_id} data-testid={`lab-patient-row-${p.patient_id}`}>
                <Td className="font-mono text-slate-700">{p.uhid}</Td>
                <Td className="font-medium text-slate-900">{p.title} {p.name}</Td>
                <Td className="text-slate-600">{p.age_display || "—"} / {p.gender?.[0]?.toUpperCase()}</Td>
                <Td className="text-slate-600">{p.phone || "—"}</Td>
                <Td className="text-slate-600">{[p.address, p.city].filter(Boolean).join(", ") || "—"}</Td>
                <Td className="text-slate-500 text-xs">{(p.created_at || "").slice(0, 10)}</Td>
                {manage && <Td><button onClick={() => openEdit(p)} className={ghostBtnCls} data-testid={`edit-patient-${p.patient_id}`}>Edit</button></Td>}
              </tr>
            )} />
          <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
            <span>{total} patients</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(p, q); }} className={ghostBtnCls}>Prev</button>
              <span className="px-3 py-2">Page {page}/{pages}</span>
              <button disabled={page >= pages} onClick={() => { const p = page + 1; setPage(p); load(p, q); }} className={ghostBtnCls}>Next</button>
            </div>
          </div>
        </>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit patient" : "Register patient"} wide testid="lab-patient-modal">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Title"><select className={inputCls} data-testid="patient-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}><option value="">—</option><option>Mr.</option><option>Mrs.</option><option>Ms.</option><option>Master</option><option>Baby</option><option>Dr.</option></select></Field>
            <Field label="Name *" className="col-span-2"><input className={inputCls} data-testid="patient-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Sex *"><select className={inputCls} data-testid="patient-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select></Field>
            <Field label="Age (years)"><input type="number" min="0" className={inputCls} data-testid="patient-age-years" value={form.age_years} onChange={(e) => setForm({ ...form, age_years: e.target.value })} /></Field>
            <Field label="Months"><input type="number" min="0" className={inputCls} data-testid="patient-age-months" value={form.age_months} onChange={(e) => setForm({ ...form, age_months: e.target.value })} /></Field>
            <Field label="Days"><input type="number" min="0" className={inputCls} data-testid="patient-age-days" value={form.age_days} onChange={(e) => setForm({ ...form, age_days: e.target.value })} /></Field>
            <Field label="Mobile"><input className={inputCls} data-testid="patient-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email"><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="City"><input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="Aadhaar / ID no."><input className={inputCls} value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value, id_type: e.target.value ? "aadhaar" : "" })} /></Field>
            <Field label="Address" className="col-span-2 sm:col-span-4"><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Medical history" className="col-span-2 sm:col-span-4"><input className={inputCls} value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} /></Field>
          </div>
          {formError && <p className="text-sm text-red-600" data-testid="patient-form-error">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-patient-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
